import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Box,
  IconButton,
  Tabs,
  Tab,
} from "@material-ui/core";
import { Delete, Add, FileCopy } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

// Explicacao das categorias — a maioria de quem usa API oficial nao sabe a
// diferenca, e escolher errado e o motivo n1 de reprovacao pela Meta.
const CATEGORIAS = [
  {
    v: "UTILITY",
    t: "Utilidade",
    cor: "#0ea5e9",
    resumo: "Confirmações, avisos e atualizações de algo que o cliente pediu",
    exemplos: [
      "Seu pedido #1234 foi enviado",
      "Sua consulta é amanhã às 14h",
      "Recebemos seu pagamento",
    ],
    quando:
      "Use quando a mensagem responde a algo que o cliente fez ou contratou. É mais barata e aprova mais fácil.",
  },
  {
    v: "MARKETING",
    t: "Marketing",
    cor: "#a855f7",
    resumo: "Promoções, novidades e convites — qualquer coisa que venda",
    exemplos: [
      "Temos 20% de desconto até domingo",
      "Chegou a coleção nova, dá uma olhada",
      "Faz tempo que você não aparece, volta pra gente",
    ],
    quando:
      "Use quando a intenção é vender ou reengajar. Custa mais caro e o cliente pode bloquear o recebimento.",
  },
  {
    v: "AUTHENTICATION",
    t: "Autenticação",
    cor: "#22c55e",
    resumo: "Somente códigos de verificação",
    exemplos: ["Seu código é 123456"],
    quando: "Use só para código de acesso. Nada de texto promocional junto.",
  },
];

// O corpo vem como string JSON da API; aqui vira objeto para exibir.
// A Meta devolve o modelo como uma lista de "components"; a PilotStatus devolve
// um objeto {header, body, footer}. A tela nasceu falando so o segundo formato,
// entao os modelos vindos da Meta apareciam como "(sem texto)".
const lerComponentesMeta = (components) => {
  const achar = (tipo) =>
    (components || []).find(
      (c) => String(c?.type || "").toUpperCase() === tipo
    );

  const cabecalho = achar("HEADER");
  const corpo = achar("BODY");
  const rodape = achar("FOOTER");
  const botoes = achar("BUTTONS");

  return {
    texto: corpo?.text || "",
    cabecalho: cabecalho?.text || "",
    rodape: rodape?.text || "",
    botoes: (botoes?.buttons || []).map((b) => b?.text).filter(Boolean),
    exemplos: {}
  };
};

const lerCorpo = (b) => {
  try {
    const o = typeof b === "string" ? JSON.parse(b) : b || {};
    return {
      texto: o?.body?.text || o?.text || "",
      rodape: o?.footer?.text || "",
      exemplos: o?.examples || {},
    };
  } catch (e) {
    return { texto: typeof b === "string" ? b : "", rodape: "", exemplos: {} };
  }
};

const corDoStatus = (st) => {
  const s = String(st || "").toUpperCase();
  if (s.includes("APPROVED")) return { bg: "#dcfce7", cor: "#15803d", txt: "Aprovado" };
  if (s.includes("REJECT")) return { bg: "#fee2e2", cor: "#b91c1c", txt: "Reprovado" };
  if (s.includes("PENDING") || s.includes("SUBMITTED"))
    return { bg: "#fef3c7", cor: "#b45309", txt: "Em análise" };
  return { bg: "#f1f5f9", cor: "#475569", txt: st || "—" };
};

const TemplatesPage = () => {
  const [conexoes, setConexoes] = useState([]);
  const [conexaoId, setConexaoId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [modal, setModal] = useState(false);
  const [aba, setAba] = useState(0);

  const [nome, setNome] = useState("");
  const [categoria, setCategoria] = useState("UTILITY");
  const [corpo, setCorpo] = useState("");
  const [rodape, setRodape] = useState("");
  // A API aceita cabecalho e botoes; a tela nao oferecia, e template sem botao
  // perde metade da utilidade (a pessoa nao tem o que tocar).
  const [cabecalho, setCabecalho] = useState("");
  const [botoesTpl, setBotoesTpl] = useState([]);
  // A Meta reprova exemplo generico do tipo "exemplo1": ela quer ver um valor
  // plausivel para cada variavel.
  const [exemplosVar, setExemplosVar] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [aberto, setAberto] = useState(null);

  const catAtual = CATEGORIAS.find((c) => c.v === categoria) || CATEGORIAS[0];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/whatsapp");
        const oficiais = (data || []).filter(
          (w) => w.channel === "pilotstatus" || w.channel === "whatsapp_official"
        );
        setConexoes(oficiais);
        if (oficiais.length) setConexaoId(oficiais[0].id);
      } catch (e) {
        toast.error("Não foi possível carregar as conexões.");
      }
    })();
  }, []);

  // A mesma tela serve aos dois caminhos: a PilotStatus e a Graph API da Meta.
  // Quem decide e o canal da conexao escolhida, nao o formato do formulario.
  const conexaoAtual = conexoes.find((c) => c.id === conexaoId);
  const ehOficial = conexaoAtual?.channel === "whatsapp_official";

  const carregar = useCallback(async () => {
    if (!conexaoId) return;
    setCarregando(true);
    try {
      const oficial =
        conexoes.find((c) => c.id === conexaoId)?.channel === "whatsapp_official";
      const url = oficial
        ? `/whatsapp-official/${conexaoId}/templates?all=1`
        : `/pilot/${conexaoId}/templates`;
      const { data } = await api.get(url);
      const lista = Array.isArray(data) ? data : data?.data || [];
      setTemplates(lista);
    } catch (e) {
      toast.error("Não foi possível carregar os modelos.");
      setTemplates([]);
    }
    setCarregando(false);
  }, [conexaoId, conexoes]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNovo = () => {
    setNome("");
    setCategoria("UTILITY");
    setCorpo("");
    setRodape("");
    setModal(true);

    setCabecalho("");

    setBotoesTpl([]);

    setExemplosVar({});
  };

  // A Meta nao permite alterar um modelo ja enviado: o caminho e criar uma
  // nova versao. Por isso "editar" abre o formulario preenchido e sugere um
  // nome novo — o antigo continua lá até você excluir.
  const abrirComBase = (t, sufixo) => {
    const c = lerCorpo(t.body);
    setNome(`${t.name}_${sufixo}`);
    setCategoria(t.category || "UTILITY");
    setCorpo(c.texto);
    setRodape(c.rodape);
    setModal(true);
  };

  const salvar = async () => {
    const n = nome.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!n) {
      toast.error("Dê um nome ao modelo.");
      return;
    }
    if (!corpo.trim()) {
      toast.error("Escreva o texto da mensagem.");
      return;
    }
    setSalvando(true);
    try {
      const variaveis = (corpo.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g) || []).map((v) =>
        v.replace(/[{}\s]/g, "")
      );
      const exemplos = {};
      const semExemplo = [];
      variaveis.forEach((v) => {
        const valor = String(exemplosVar[v] || "").trim();
        if (valor) exemplos[v] = valor;
        else semExemplo.push(v);
      });
      if (semExemplo.length) {
        toast.error(
          `Dê um exemplo real para: ${semExemplo.join(", ")}. A Meta reprova sem isso.`
        );
        setSalvando(false);
        return;
      }

      const botoesLimpos = botoesTpl
        .filter((b) => String(b.text || "").trim())
        .slice(0, 10)
        .map((b) =>
          b.type === "URL"
            ? { type: "URL", text: b.text.trim(), url: String(b.url || "").trim() }
            : b.type === "PHONE_NUMBER"
            ? {
                type: "PHONE_NUMBER",
                text: b.text.trim(),
                phone_number: String(b.phone || "").trim()
              }
            : { type: "QUICK_REPLY", text: b.text.trim() }
        );

      const rotaCriar = ehOficial
        ? `/whatsapp-official/${conexaoId}/templates`
        : `/pilot/${conexaoId}/templates`;
      await api.post(rotaCriar, {
        name: n,
        category: categoria,
        language: "pt_BR",
        body: {
          ...(cabecalho.trim()
            ? { header: { type: "TEXT", text: cabecalho.trim() } }
            : {}),
          body: { text: corpo.trim() },
          ...(rodape.trim() ? { footer: { text: rodape.trim() } } : {}),
          ...(botoesLimpos.length ? { buttons: botoesLimpos } : {}),
        },
        examples: exemplos,
      });
      toast.success("Modelo enviado para análise da Meta.");
      setModal(false);
      carregar();
    } catch (e) {
      toast.error(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Erro ao criar o modelo."
      );
    }
    setSalvando(false);
  };

  const excluir = async (t) => {
    if (!window.confirm(`Excluir o modelo "${t.name}"?`)) return;
    try {
      const rotaExcluir = ehOficial
        ? `/whatsapp-official/${conexaoId}/templates/${encodeURIComponent(t.name)}`
        : `/pilot/${conexaoId}/templates/${t.id}`;
      await api.delete(rotaExcluir);
      toast.success("Modelo excluído.");
      carregar();
    } catch (e) {
      toast.error("Erro ao excluir.");
    }
  };

  const dica = { fontSize: 11.5, color: "#6b7280", display: "block", marginTop: 4, lineHeight: 1.5 };

  return (
    <div style={{ padding: 24 }}>
      <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 700 }}>
            Modelos de mensagem
          </Typography>
          <Typography style={dica}>
            Na API oficial, para falar com alguém que não te escreveu nas últimas 24
            horas você precisa de um modelo aprovado pela Meta. É aqui que você cria e
            acompanha.
          </Typography>
        </div>
        <Box style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {conexoes.length > 1 && (
            <TextField
              select
              size="small"
              variant="outlined"
              value={conexaoId}
              onChange={(e) => setConexaoId(e.target.value)}
              style={{ minWidth: 190 }}
            >
              {conexoes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={abrirNovo}
            disabled={!conexaoId}
            style={{ textTransform: "none" }}
          >
            Novo modelo
          </Button>
        </Box>
      </Box>

      <Tabs
        value={aba}
        onChange={(e, v) => setAba(v)}
        indicatorColor="primary"
        textColor="primary"
        style={{ marginTop: 16 }}
      >
        <Tab label={`Meus modelos (${templates.length})`} />
        <Tab label="Entender as categorias" />
      </Tabs>

      {aba === 0 && (
        <>
          {!conexoes.length && (
            <Paper style={{ padding: 24, marginTop: 16, textAlign: "center", color: "#6b7280" }}>
              Nenhuma conexão de API oficial encontrada. Conecte um número em Canais para
              criar modelos.
            </Paper>
          )}

          {conexoes.length > 0 && !templates.length && !carregando && (
            <Paper style={{ padding: 24, marginTop: 16, textAlign: "center", color: "#6b7280" }}>
              Nenhum modelo criado ainda. Clique em "Novo modelo" para começar.
            </Paper>
          )}

          {templates.map((t) => {
            const st = corDoStatus(t.metaStatus || t.status);
            return (
              <Paper key={t.id} style={{ padding: 14, marginTop: 12 }}>
                <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Box style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <Typography style={{ fontWeight: 700 }}>{t.name}</Typography>
                      <Chip
                        size="small"
                        label={st.txt}
                        style={{ backgroundColor: st.bg, color: st.cor, fontWeight: 600 }}
                      />
                      <Chip
                        size="small"
                        label={(CATEGORIAS.find((c) => c.v === t.category) || {}).t || t.category}
                        style={{ backgroundColor: "#f1f5f9", color: "#475569" }}
                      />
                      {t.language && (
                        <Typography style={{ fontSize: 11, color: "#94a3b8" }}>{t.language}</Typography>
                      )}
                    </Box>
                    {t.rejectedReason && (
                      <Typography style={{ ...dica, color: "#b91c1c" }}>
                        Motivo da reprovação: {t.rejectedReason}
                      </Typography>
                    )}
                  </div>
                  <Box style={{ whiteSpace: "nowrap" }}>
                    <Button
                      size="small"
                      onClick={() => setAberto(aberto === t.id ? null : t.id)}
                      style={{ textTransform: "none", fontSize: 12 }}
                    >
                      {aberto === t.id ? "Ocultar" : "Ver mensagem"}
                    </Button>
                    <IconButton
                      size="small"
                      title="Copiar o nome (use no bloco de Template do fluxo)"
                      onClick={() => {
                        if (navigator.clipboard) navigator.clipboard.writeText(t.name);
                        toast.success("Nome copiado.");
                      }}
                    >
                      <FileCopy fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => excluir(t)}>
                      <Delete fontSize="small" style={{ color: "#dc2626" }} />
                    </IconButton>
                  </Box>
                </Box>

                {aberto === t.id && (() => {
                  // Modelo da Meta vem com "components"; o da PilotStatus com "body".
                  const c = Array.isArray(t.components)
                    ? lerComponentesMeta(t.components)
                    : lerCorpo(t.body);
                  return (
                    <Box style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
                      <Typography style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 6 }}>
                        Como o cliente recebe:
                      </Typography>
                      <Box
                        style={{
                          background: "#dcf8c6",
                          borderRadius: 10,
                          padding: "10px 12px",
                          maxWidth: 420,
                          whiteSpace: "pre-wrap",
                          fontSize: 13.5,
                          color: "#111827",
                        }}
                      >
                        {c.cabecalho ? (
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>
                            {c.cabecalho}
                          </div>
                        ) : null}
                        {c.texto || "(sem texto)"}
                        {c.rodape ? (
                          <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 8 }}>
                            {c.rodape}
                          </div>
                        ) : null}
                        {(c.botoes || []).length > 0 ? (
                          <div style={{ marginTop: 8, borderTop: "1px solid #b7e0a0", paddingTop: 6 }}>
                            {c.botoes.map((b, i) => (
                              <div key={i} style={{ fontSize: 12.5, color: "#1d4ed8", textAlign: "center", padding: "3px 0" }}>
                                {b}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </Box>

                      {(t.variables || []).length > 0 && (
                        <Typography style={{ fontSize: 11.5, color: "#6b7280", marginTop: 8 }}>
                          Campos que você preenche no envio:{" "}
                          <b>{(t.variables || []).join(", ")}</b>
                        </Typography>
                      )}

                      {t.sendable === false && (
                        <Typography style={{ fontSize: 11.5, color: "#b45309", marginTop: 6 }}>
                          Este modelo ainda não pode ser enviado — aguardando aprovação da Meta.
                        </Typography>
                      )}

                      <Box style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => abrirComBase(t, "v2")}
                          style={{ textTransform: "none", fontSize: 12 }}
                        >
                          Editar e reenviar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => abrirComBase(t, "b")}
                          style={{ textTransform: "none", fontSize: 12 }}
                        >
                          Duplicar para testar variação
                        </Button>
                      </Box>
                      <Typography style={{ fontSize: 11, color: "#94a3b8", marginTop: 6, lineHeight: 1.5 }}>
                        A Meta não deixa alterar um modelo já enviado — as duas opções criam
                        uma nova versão com o texto já preenchido. O modelo antigo continua
                        aqui até você excluir.
                      </Typography>

                      <Box style={{ marginTop: 10, padding: 10, background: "#eff6ff", borderRadius: 6 }}>
                        <Typography style={{ fontSize: 11.5, color: "#1e40af", lineHeight: 1.6 }}>
                          Reprovado ou classificado numa categoria que você não pediu? A
                          aprovação da Meta é automática e erra com frequência — dá para
                          pedir revisão humana, e costuma ser aceita.
                        </Typography>
                        <a
                          href="https://business.facebook.com/wa/manage/message-templates/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#1d4ed8",
                            display: "inline-block",
                            marginTop: 6,
                          }}
                        >
                          Pedir revisão no WhatsApp Manager →
                        </a>
                      </Box>
                    </Box>
                  );
                })()}
              </Paper>
            );
          })}
        </>
      )}

      {aba === 1 && (
        <Box style={{ marginTop: 16 }}>
          <Paper style={{ padding: 16, marginBottom: 14, background: "#f8fafc" }}>
            <Typography style={{ fontSize: 13, lineHeight: 1.7, color: "#334155" }}>
              A Meta cobra e avalia cada modelo conforme a <b>categoria</b>. Escolher a
              errada é o motivo mais comum de reprovação — e mandar promoção como
              "utilidade" pode custar restrição na conta. Na dúvida, leia os exemplos
              abaixo.
            </Typography>
          </Paper>

          {CATEGORIAS.map((c) => (
            <Paper key={c.v} style={{ padding: 16, marginBottom: 12, borderLeft: `4px solid ${c.cor}` }}>
              <Typography style={{ fontWeight: 700, color: c.cor }}>{c.t}</Typography>
              <Typography style={{ fontSize: 13, color: "#334155", marginTop: 4 }}>
                {c.resumo}
              </Typography>
              <Typography style={{ ...dica, marginTop: 8 }}>Exemplos:</Typography>
              {c.exemplos.map((e, i) => (
                <Typography
                  key={i}
                  style={{ fontSize: 12.5, color: "#475569", marginLeft: 10, marginTop: 2 }}
                >
                  • “{e}”
                </Typography>
              ))}
              <Typography style={{ ...dica, marginTop: 8, color: "#0f172a" }}>
                {c.quando}
              </Typography>
            </Paper>
          ))}

          <Paper style={{ padding: 16, background: "#fff7ed", border: "1px solid #fed7aa" }}>
            <Typography style={{ fontSize: 13, fontWeight: 700, color: "#9a3412" }}>
              A regra das 24 horas
            </Typography>
            <Typography style={{ fontSize: 12.5, color: "#7c2d12", lineHeight: 1.7, marginTop: 4 }}>
              Depois que o cliente te manda uma mensagem, você tem 24 horas para responder
              o que quiser, sem modelo. Passado esse tempo, só consegue falar com ele por
              um modelo aprovado. Por isso vale ter pelo menos um modelo de utilidade
              pronto para retomar conversas.
            </Typography>
          </Paper>
        </Box>
      )}

      <Dialog open={modal} onClose={() => setModal(false)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>Novo modelo de mensagem</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome do modelo"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="retomada_atendimento"
          />
          <Typography style={dica}>
            Só letras minúsculas, números e underline. É o nome que você vai escolher no
            bloco de Template do fluxo — o cliente nunca vê.
          </Typography>

          <TextField
            select
            label="Categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            style={{ marginTop: 16 }}
          >
            {CATEGORIAS.map((c) => (
              <MenuItem key={c.v} value={c.v}>
                {c.t}
              </MenuItem>
            ))}
          </TextField>
          <Box
            style={{
              background: "#f8fafc",
              borderLeft: `3px solid ${catAtual.cor}`,
              padding: 10,
              marginTop: 8,
              borderRadius: 4,
            }}
          >
            <Typography style={{ fontSize: 12.5, color: "#334155" }}>
              {catAtual.resumo}
            </Typography>
            <Typography style={{ fontSize: 11.5, color: "#64748b", marginTop: 4 }}>
              Ex.: “{catAtual.exemplos[0]}”
            </Typography>
          </Box>

          <TextField
            label="Cabeçalho (opcional)"
            value={cabecalho}
            onChange={(e) => setCabecalho(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            style={{ marginTop: 16 }}
            inputProps={{ maxLength: 60 }}
            placeholder="Seu pedido chegou!"
          />
          <Typography style={dica}>
            Linha em negrito no topo da mensagem. Até 60 caracteres.
          </Typography>

          <TextField
            label="Mensagem"
            value={corpo}
            onChange={(e) => setCorpo(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            size="small"
            variant="outlined"
            style={{ marginTop: 16 }}
            placeholder="Oi {{nome}}! Passando para lembrar do seu atendimento."
          />
          <Typography style={dica}>
            Use <b>{"{{nome}}"}</b> para partes que mudam a cada envio. Cada variável vira
            um campo na hora de enviar. Evite escrever "clique aqui" ou usar caixa alta em
            excesso — a Meta reprova.
          </Typography>

          <TextField
            label="Rodapé (opcional)"
            value={rodape}
            onChange={(e) => setRodape(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            style={{ marginTop: 16 }}
            placeholder="Responda SAIR para não receber mais"
          />

          {/* Exemplos das variáveis — a Meta reprova sem valor plausível */}
          {(corpo.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g) || []).length > 0 && (
            <Box style={{ marginTop: 18, background: "#fffbeb", padding: 12, borderRadius: 6, border: "1px solid #fde68a" }}>
              <Typography style={{ fontSize: 13, fontWeight: 600, color: "#92400e" }}>
                Exemplo de cada variável
              </Typography>
              <Typography style={{ fontSize: 11.5, color: "#92400e", marginBottom: 8 }}>
                A Meta lê estes valores para entender a mensagem. Use algo real —
                "exemplo1" costuma ser reprovado.
              </Typography>
              {[...new Set(
                (corpo.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g) || []).map((v) =>
                  v.replace(/[{}\s]/g, "")
                )
              )].map((v) => (
                <Box key={v} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                  <Typography style={{ fontSize: 12, minWidth: 90, fontFamily: "monospace" }}>
                    {`{{${v}}}`}
                  </Typography>
                  <TextField
                    value={exemplosVar[v] || ""}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setExemplosVar((p) => ({ ...p, [v]: valor }));
                    }}
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder={v === "nome" ? "Maria" : "valor de exemplo"}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Botões */}
          <Box style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
            <Typography style={{ fontSize: 13, fontWeight: 600 }}>
              Botões (opcional, até 10)
            </Typography>
            <Typography style={dica}>
              <b>Resposta rápida</b> devolve o toque para o fluxo.{" "}
              <b>Link</b> abre um endereço. <b>Telefone</b> liga.
            </Typography>

            {botoesTpl.map((b, i) => (
              <Box key={i} style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "flex-start" }}>
                <TextField
                  select
                  value={b.type}
                  onChange={(e) => {
                    const l = [...botoesTpl];
                    l[i] = { ...l[i], type: e.target.value };
                    setBotoesTpl(l);
                  }}
                  size="small"
                  variant="outlined"
                  style={{ width: 140 }}
                >
                  <MenuItem value="QUICK_REPLY">Resposta rápida</MenuItem>
                  <MenuItem value="URL">Link</MenuItem>
                  <MenuItem value="PHONE_NUMBER">Telefone</MenuItem>
                </TextField>
                <TextField
                  placeholder="Texto do botão"
                  value={b.text || ""}
                  onChange={(e) => {
                    const l = [...botoesTpl];
                    l[i] = { ...l[i], text: e.target.value };
                    setBotoesTpl(l);
                  }}
                  size="small"
                  variant="outlined"
                  inputProps={{ maxLength: 25 }}
                  style={{ width: 150 }}
                />
                {b.type === "URL" && (
                  <TextField
                    placeholder="https://..."
                    value={b.url || ""}
                    onChange={(e) => {
                      const l = [...botoesTpl];
                      l[i] = { ...l[i], url: e.target.value };
                      setBotoesTpl(l);
                    }}
                    size="small"
                    variant="outlined"
                    style={{ flex: 1 }}
                  />
                )}
                {b.type === "PHONE_NUMBER" && (
                  <TextField
                    placeholder="+5511999999999"
                    value={b.phone || ""}
                    onChange={(e) => {
                      const l = [...botoesTpl];
                      l[i] = { ...l[i], phone: e.target.value };
                      setBotoesTpl(l);
                    }}
                    size="small"
                    variant="outlined"
                    style={{ flex: 1 }}
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => setBotoesTpl(botoesTpl.filter((_, x) => x !== i))}
                >
                  <Delete fontSize="small" style={{ color: "#dc2626" }} />
                </IconButton>
              </Box>
            ))}

            {botoesTpl.length < 10 && (
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() =>
                  setBotoesTpl([...botoesTpl, { type: "QUICK_REPLY", text: "" }])
                }
                style={{ textTransform: "none", marginTop: 8 }}
              >
                Adicionar botão
              </Button>
            )}
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModal(false)}>Cancelar</Button>
          <Button onClick={salvar} color="primary" variant="contained" disabled={salvando}>
            {salvando ? "Enviando..." : "Enviar para análise"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
